import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApolloError, useQuery } from "@apollo/client";

import { getRemoteAsset } from "../api/queries/getRemoteAsset";
import {
    type GetRemoteAssetQuery,
    type GetRemoteAssetQueryVariables,
} from "../api/queries/getRemoteAsset.gql-generated";

import { LENS_USAGE_LIMIT, type LensUsage } from "./remoteAssetLensUsageShared";

interface RemoteAssetResult {
    asset: GetRemoteAssetQuery["remoteAsset"] | null;
    lenses: readonly LensUsage[];
    hasMoreLenses: boolean;
    fetchMoreLenses: () => void;
    loadingMoreLenses: boolean;
    loadMoreLensesError: ApolloError | undefined;
    loading: boolean;
    error: ApolloError | undefined;
}

export const useRemoteAsset = (assetId: string | null): RemoteAssetResult => {
    const assetIdRef = useRef(assetId);
    assetIdRef.current = assetId;

    const [loadingMoreLenses, setLoadingMoreLenses] = useState(false);
    const [loadMoreLensesError, setLoadMoreLensesError] = useState<ApolloError | undefined>();
    const variables = useMemo(
        (): GetRemoteAssetQueryVariables => ({
            id: assetId ?? "",
            lensUsageCursor: null,
            lensUsageLimit: LENS_USAGE_LIMIT,
        }),
        [assetId],
    );

    const { data, loading, error, fetchMore } = useQuery<GetRemoteAssetQuery, GetRemoteAssetQueryVariables>(
        getRemoteAsset,
        {
            variables,
            skip: !assetId,
            fetchPolicy: "cache-and-network",
            nextFetchPolicy: "cache-first",
        },
    );

    useEffect(() => {
        setLoadMoreLensesError(undefined);
    }, [assetId]);

    const connection = data?.remoteAsset?.usedByLensesConnection;
    const lenses = connection?.nodes ?? [];
    const cursor = connection?.cursor ?? null;

    const fetchMoreLenses = useCallback(() => {
        if (!assetId || !cursor || loadingMoreLenses) return;

        setLoadingMoreLenses(true);
        setLoadMoreLensesError(undefined);
        void fetchMore({
            variables: {
                id: assetId,
                lensUsageCursor: cursor,
                lensUsageLimit: LENS_USAGE_LIMIT,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const previousAsset = previousResult.remoteAsset;
                const nextConnection = fetchMoreResult.remoteAsset?.usedByLensesConnection;

                if (!previousAsset || !nextConnection || assetIdRef.current !== assetId) return previousResult;

                return {
                    ...previousResult,
                    remoteAsset: {
                        ...previousAsset,
                        usedByLensesConnection: {
                            ...nextConnection,
                            nodes: [...previousAsset.usedByLensesConnection.nodes, ...nextConnection.nodes],
                        },
                    },
                };
            },
        })
            .catch((nextError: unknown) => {
                if (nextError instanceof ApolloError) {
                    setLoadMoreLensesError(nextError);
                    return;
                }

                setLoadMoreLensesError(
                    new ApolloError({
                        networkError: nextError instanceof Error ? nextError : new Error("Failed to load more lenses"),
                    }),
                );
            })
            .finally(() => {
                setLoadingMoreLenses(false);
            });
    }, [assetId, cursor, fetchMore, loadingMoreLenses]);

    return {
        asset: data?.remoteAsset ?? null,
        lenses,
        hasMoreLenses: cursor !== null,
        fetchMoreLenses,
        loadingMoreLenses,
        loadMoreLensesError,
        loading,
        error,
    };
};
